import { mg } from '../mg/mg';
import { toasterSvc } from './ToasterSvc';

interface IAuthStore {
	readonly baseUrl: string
	readonly role: string
	readonly email: string
	readonly authorization: string
	setAuthorization(authorization: string) : void
	readonly roles: ReadonlyArray<string>
	setRoles(roles: ReadonlyArray<string>) : void
}

interface IReoctoStore {
	readonly useNet: boolean
	setIsOffline(offline: boolean) : void
}

type IOutputObject = {[key: string]: any}
export type IOcResponseOutputObject = IOutputObject

export type IFeedbackLevel = 'INFO' | 'WARNING' | 'ERROR';

export interface IFeedback {
	readonly text: string
	readonly level: IFeedbackLevel
}

export interface IOcResponse {
	request: {
		method: 'GET' | 'POST'
		path: string // just e.g., "Login", no slashes, no query string
		params: object // always present
		etag: string // expected etag (as used in If-None-Match), may be empty
	}
	etag: string
	outputList?: Array<IOutputObject> // only defined on success
	outputObject?: IOutputObject // only defined when outputList.length===1
	failure?: { // only defined on failure
		type: string // what went wrong
		subject: string // what the problem concerns
		info: string // may be empty
		feedbacks?: Array<IFeedback> // e.g., list of all import problems
		translation: {
			title: string
			body: string // show this to the user unless you can handle it better
			support: string
		};
	},
	blob?: Blob,
}

function apiVersion(path: string) {
	switch (path) {
	default:
		return 2;
	}
}

function apiUrl(authStore: IAuthStore, path: string) {
	const v = apiVersion(path);
	const middle = v ? '/api/v' + v + '/' : '/api/';
	return authStore.baseUrl + middle + path;
}

function makeResponse(request: IOcResponse['request'], response: Response, responseText: string) : IOcResponse {
	const etag = response.headers.get('etag') || '';
	let outputList: IOcResponse['outputList'];
	let outputObject: IOcResponse['outputObject'];
	let failure: IOcResponse['failure'];
	if (responseText) {
		try {
			const object = mg.parseJson(responseText) as any;
			outputList = object.outputList;
			outputObject = outputList && outputList.length===1 ? outputList[0] : undefined;
			failure = object.failure;
		} catch (e) {
			console.error('Error in parsing response', responseText);
		}
	}
	return {request, etag, outputList, outputObject, failure};
}

function makeOfflineResponse(request: IOcResponse['request'], reoctoStore: IReoctoStore) :	IOcResponse {
	reoctoStore.setIsOffline(true);
	const failure = {
		type: 'OFFLINE',
		subject: 'UNSPECIFIED',
		info: '',
		translation: {title: 'OFFLINE', body: 'OFFLINE', support: ''}
	};
	return {request, etag: '', outputList: undefined, outputObject: undefined, failure};
}

const stdHeaders = (authStore: IAuthStore) : RequestInit => ({
	mode: 'cors',
	headers: {authorization: authStore.authorization, 'x-role': authStore.role},
	credentials: window.location.hostname.match(/local(?:\.|host)/) ? 'include' : 'same-origin',
});

const handleStatus = (status: number, headers: Headers, reoctoStore: IReoctoStore, authStore: IAuthStore) : void => {
	reoctoStore.setIsOffline(!status);
	if (!status) return;
	const authorization = headers && headers.get('authorization') || '';
	if (status === 401) {
		authStore.setAuthorization('');
	} else if (authorization && authorization.length>2) {
		authStore.setAuthorization(authorization);
	}
};

const transform = (request: IOcResponse['request'], response: Response, reoctoStore: IReoctoStore, authStore: IAuthStore) : Promise<IOcResponse> => {
	handleStatus(response.status || 0, response.headers, reoctoStore, authStore);
	if (!response.text) return Promise.reject(response);
	const etag = response.headers.get('etag');
	const textPromise = etag && request.etag===etag ? Promise.resolve('') : response.text();
	return textPromise
		.then(responseText => makeResponse(request, response, responseText));
};

export class Communicator {
	constructor(private readonly reoctoStore: () => IReoctoStore, private readonly authStore: () => IAuthStore) {
	}

	doGet = (path: string, params: {}, etag?: string) : Promise<IOcResponse> => {
		const method = 'GET';
		etag = etag || '';
		const request: IOcResponse['request'] = {path, method, params, etag};
		if (!this.reoctoStore().useNet) return Promise.resolve(makeOfflineResponse(request, this.reoctoStore()));
		const queryString = params
			? Object.keys(params).map(key => [key, params[key]].map(encodeURIComponent).join('=')).join('&') : '';
		return fetch(apiUrl(this.authStore(), path) + '?' + queryString, {method, ...stdHeaders(this.authStore())})
			.then(response => transform(request, response, this.reoctoStore(), this.authStore()))
			.catch(() => Promise.resolve(makeOfflineResponse(request, this.reoctoStore())))
			.then(response => {
				if (response.failure && response.failure.type!=='UNAUTHORIZED_NO_USER') toasterSvc.handleResponse('')(response);
				return response;
			});
	}

	doPost = (path: string, params: {}) : Promise<IOcResponse> => {
		const method = 'POST';
		const request: IOcResponse['request'] = {path, method, params, etag: ''};
		if (!this.reoctoStore().useNet) return Promise.resolve(makeOfflineResponse(request, this.reoctoStore()));
		return fetch(apiUrl(this.authStore(), path), {method, ...stdHeaders(this.authStore()), body: JSON.stringify(params)})
			.then(response => transform(request, response, this.reoctoStore(), this.authStore()))
			.catch(() => Promise.resolve(makeOfflineResponse(request, this.reoctoStore())));
	}

	doDownload = async (path: string, params: Record<string, string|number>) : Promise<IOcResponse> => {
		const method = 'POST';
		const request: IOcResponse['request'] = {path, method, params, etag: ''};
		if (!this.reoctoStore().useNet) return Promise.resolve(makeOfflineResponse(request, this.reoctoStore()));
		try {
			const response = await fetch(apiUrl(this.authStore(), path), {method, ...stdHeaders(this.authStore()), body: JSON.stringify(params)});
			handleStatus(response.status || 0, response.headers, this.reoctoStore(), this.authStore());
			const blob = await response.blob();
			return {request, etag: '', blob};
		} catch (e) {
			return Promise.resolve(makeOfflineResponse(request, this.reoctoStore()));
		}
	}

	doUpload = (path: string, params: {}, file: File) : Promise<IOcResponse> => {
		const method = 'POST';
		const request: IOcResponse['request'] = {path, method, params, etag: ''};
		if (!this.reoctoStore().useNet) return Promise.resolve(makeOfflineResponse(request, this.reoctoStore()));
		const queryString = params
			? Object.keys(params).map(key => [key, params[key]].map(encodeURIComponent).join('=')).join('&') : '';
		return fetch(apiUrl(this.authStore(), path) + '?' + queryString, {method, ...stdHeaders(this.authStore()), body: file})
			.then(response => transform(request, response, this.reoctoStore(), this.authStore()))
			.catch(() => Promise.resolve(makeOfflineResponse(request, this.reoctoStore())));
	}

	login = (password: string) : Promise<IOcResponse> => this.doPost('Login', {email: this.authStore().email, password})
		.then(r => {
			if (r.outputList) {
				const roles = new Set(r.outputList.map(i => i.type));
				this.authStore().setRoles([...roles]);
			}
			return r;
		});

	logout = () : Promise<IOcResponse> => {
		const result = this.doPost('Logout', {});
		this.authStore().setAuthorization('');
		window.location.reload();
		return result;
	}

	//TODO 1 remove requireOutputList and two more
	/** @deprecated */
	requireOutputList(response: IOcResponse) : Promise<IOcResponse & {outputList: Array<IOutputObject>}> {
		return response.outputList ? Promise.resolve(response) as any : Promise.reject(response);
	}

	/** @deprecated */
	requireOutputObject(response: IOcResponse) : Promise<IOcResponse & {outputObject: IOutputObject}> {
		return response.outputObject ? Promise.resolve(response) as any : Promise.reject(response);
	}

	/** @deprecated */
	resolveIfPossible(responseOrError: any) : Promise<IOcResponse> {
		const r = responseOrError;
		return r && r.request && r.request.path ? Promise.resolve(r) : Promise.reject(r);
	}
}
